import { Box, Button, Card, DataList, Flex, Separator } from '@radix-ui/themes';
import React from 'react';
import { Trans } from 'react-i18next';
import { TextBlockSkeleton } from '../Skeletons/TextBlockSkeleton';
import { Title } from '../Title';
import { TruncatedTooltip } from '../TruncatedTooltip';
import { useIsMobile } from '@common/hooks';

export type FieldConfig<T> = {
    [K in keyof T]-?: {
        key: K;
        formatter?: (value: NonNullable<T[K]>) => React.ReactNode;
    };
}[keyof T];

export interface DataEntry {
    label: string;
    value: React.ReactNode;
}

export interface MetadataCardBaseProps {
    title: string;
    columns?: number;
    rows?: number;
    isLoading?: boolean;
}

export interface MetadataCardProps extends MetadataCardBaseProps {
    data: DataEntry[];
}

export const MetadataCard = ({
    title,
    data,
    columns = 2,
    rows = 3,
    isLoading = false,
}: MetadataCardProps) => {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const isMobile = useIsMobile();

    const activeColumns = isMobile ? 1 : columns;
    const visibleData = isExpanded ? data : data.slice(0, activeColumns * rows);

    const columnsData = Array.from({ length: activeColumns }, (_, colIndex) =>
        visibleData.filter((_, index) => index % activeColumns === colIndex),
    );

    return (
        <Card variant={'surface'} size={'5'} data-radius={'none'}>
            <Title mb={'4'}>{title}</Title>

            <Box style={{ flexGrow: 1 }}>
                <Flex direction={{ initial: 'column', sm: 'row' }} gap={'6'}>
                    {columnsData.map((colData, colIndex) => {
                        if (colData.length === 0) return null;

                        return (
                            <React.Fragment key={colIndex}>
                                <Box style={{ flex: 1, minWidth: 0 }}>
                                    <DataList.Root
                                        orientation={'vertical'}
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 'var(--space-4)',
                                        }}
                                    >
                                        {colData.map(
                                            ({ label, value }, rowIndex) => (
                                                <Box
                                                    key={`${colIndex}-${rowIndex}`}
                                                >
                                                    <TextBlockSkeleton
                                                        loading={isLoading}
                                                        size={1}
                                                    >
                                                        <DataList.Item>
                                                            <DataList.Label minWidth='88px'>
                                                                {label}
                                                            </DataList.Label>
                                                            <DataList.Value>
                                                                <TruncatedTooltip>
                                                                    {value ??
                                                                        '-'}
                                                                </TruncatedTooltip>
                                                            </DataList.Value>
                                                        </DataList.Item>
                                                    </TextBlockSkeleton>
                                                </Box>
                                            ),
                                        )}
                                    </DataList.Root>
                                </Box>

                                {colIndex < activeColumns - 1 && (
                                    <Box>
                                        <Separator
                                            orientation={'vertical'}
                                            size={'4'}
                                        />
                                    </Box>
                                )}
                            </React.Fragment>
                        );
                    })}
                </Flex>
            </Box>

            <Flex justify={'end'} mt={'4'} flexShrink={'0'}>
                <Button
                    variant={'ghost'}
                    color='gray'
                    onClick={() => setIsExpanded(!isExpanded)}
                    disabled={isLoading}
                >
                    <Trans
                        i18nKey={
                            isExpanded
                                ? 'common:pagination.seeLess'
                                : 'common:pagination.seeMore_other'
                        }
                        values={{ itemCount: data.length }}
                    />
                </Button>
            </Flex>
        </Card>
    );
};
